import { createErrorTransformer } from 'store/queries/utils.ts'
import { Flow } from 'types/logging.ts'
import { boltBackend } from '../index.ts'
import { BOLT_BACKEND_TAGS } from 'store/queries/bolt-backend/constants.ts'
import {
  ExportProspectReportQueryStringParameters,
  GetProspectReportsQueryStringParameters,
  GetProspectReportsResponseBody,
  ExportBillingDetailsReportQueryStringParameters,
  GetBillingDetailsReportsQueryStringParameters,
  GetBillingDetailsReportsResponseBody,
  GetServiceContractReportsQueryStringParameters,
  GetServiceContractReportsResponseBody,
  ExportServiceContractReportQueryStringParameters
} from '@boltenergy-be/shared'
import { stringify } from 'qs'

export const reportsBoltBackend = boltBackend.injectEndpoints({
  endpoints: (builder) => ({
    // PROSPECT REPORTS
    getProspectReports: builder.query<GetProspectReportsResponseBody, GetProspectReportsQueryStringParameters>({
      query: ({ from, to }) => {
        const query = stringify({ from, to }, { addQueryPrefix: true, skipNulls: true })
        return `/reports/prospects${query}`
      },
      transformResponse: ({ data }) => data,
      transformErrorResponse: createErrorTransformer(`[${Flow.REPORTS}:getProspectReports]`),
      providesTags: [BOLT_BACKEND_TAGS.salesReports]
    }),
    getProspectReportExport: builder.query<string, ExportProspectReportQueryStringParameters>({
      query: ({ from, to, report }) => {
        const query = stringify({ from, to, report }, { addQueryPrefix: true, skipNulls: true })

        // The endpoint answers with a raw CSV body, which the base query's default JSON handler would reject as a PARSING_ERROR
        return {
          url: `/reports/prospects/export${query}`,
          responseHandler: 'text',
          headers: { Accept: 'text/csv' }
        }
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.REPORTS}:getProspectReportExport]`)
    }),

    // SERVICE CONTRACT REPORTS
    getServiceContractReports: builder.query<GetServiceContractReportsResponseBody, GetServiceContractReportsQueryStringParameters>({
      query: ({ from, to }) => {
        const query = stringify({ from, to }, { addQueryPrefix: true, skipNulls: true })
        return `/reports/service-contracts${query}`
      },
      transformResponse: ({ data }) => data,
      transformErrorResponse: createErrorTransformer(`[${Flow.REPORTS}:getServiceContractReports]`),
      providesTags: [BOLT_BACKEND_TAGS.salesReports]
    }),
    getServiceContractReportExport: builder.query<string, ExportServiceContractReportQueryStringParameters>({
      query: ({ from, to, report }) => {
        const query = stringify({ from, to, report }, { addQueryPrefix: true, skipNulls: true })

        // The endpoint answers with a raw CSV body, which the base query's default JSON handler would reject as a PARSING_ERROR
        return {
          url: `/reports/service-contracts/export${query}`,
          responseHandler: 'text',
          headers: { Accept: 'text/csv' }
        }
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.REPORTS}:getServiceContractReportExport]`)
    }),

    // BILLING DETAIL REPORTS
    getBillingDetailsReports: builder.query<GetBillingDetailsReportsResponseBody, GetBillingDetailsReportsQueryStringParameters>({
      query: ({ from, to }) => {
        const query = stringify({ from, to }, { addQueryPrefix: true, skipNulls: true })
        return `/reports/service-contracts/billing-details${query}`
      },
      transformResponse: ({ data }) => data,
      transformErrorResponse: createErrorTransformer(`[${Flow.REPORTS}:getBillingDetailsReports]`),
      providesTags: [BOLT_BACKEND_TAGS.salesReports]
    }),
    getBillingDetailsReportExport: builder.query<string, ExportBillingDetailsReportQueryStringParameters>({
      query: ({ from, to, report }) => {
        const query = stringify({ from, to, report }, { addQueryPrefix: true, skipNulls: true })

        // The endpoint answers with a raw CSV body, which the base query's default JSON handler would reject as a PARSING_ERROR
        return {
          url: `/reports/service-contracts/billing-details/export${query}`,
          responseHandler: 'text',
          headers: { Accept: 'text/csv' }
        }
      },
      transformErrorResponse: createErrorTransformer(`[${Flow.REPORTS}:getBillingDetailsExport]`)
    })
  })
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  // Queries
  useGetProspectReportsQuery,
  useGetBillingDetailsReportsQuery,
  useGetServiceContractReportsQuery,
  useLazyGetProspectReportExportQuery,
  useLazyGetBillingDetailsReportExportQuery,
  useLazyGetServiceContractReportExportQuery
} = reportsBoltBackend
